import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import axios, { AxiosResponse } from 'axios';
import config from 'constants/apiConfig';
import {
  selectGroupGuid,
  selectUserGuid,
} from '../../../slices/commonSelectors';

export const keys = {
  base: ['sitrepsV2'] as const,
  allByGroup: (group_guid: UUID) => [...keys.base, group_guid] as const,
  byId: (sitrepId: UUID | undefined) => [...keys.base, sitrepId] as const,
};

export type DataSource = {
  id: UUID;
  description?: string;
  source: string;
};
type OutputFormatStructured = {
  summary_options: string[];
};
type SectionVersion = {
  data_string: string | null;
  sitrep_section_text: string | null;
  status: string;
  timestamp: string;
  version_id: UUID;
};
export type SitrepSectionType = {
  id: UUID;
  ai_instructions: string;
  data_source: DataSource[];
  data_type_source: string;
  name: string;
  order: number;
  output_format: string;
  output_format_structured: OutputFormatStructured;
  section_id: UUID; // this is the same as the ID
  section_versions: SectionVersion[];
  type: string;
};
export type GetAllSitrepsForGroupResponse = {
  id: UUID;
  status: string;
  sitrep_source: string;
  sitrep_source_id: UUID;
  sitrep_template_id: UUID;
  name: string;
  timestamp: string;
  user_guid: UUID;
  group_guid: UUID;
  error_message?: string;
  archived: boolean;
  aor_id: UUID | null;
  description: string;
  incident_id: UUID;
};

export type PollSitrepResponse = GetAllSitrepsForGroupResponse & {
  sections: SitrepSectionType[];
};
const fetchSitrepsByGroup = async (group_guid: UUID) => {
  const response = await axios.get<GetAllSitrepsForGroupResponse[]>(
    `${config.apiGateway.sitrepsV2}/`,
    {
      params: { group_guid },
    }
  );
  return response.data;
};
// Hook to fetch sitreps (list of sitreps)
export const useSitrepsV2 = () => {
  const group_guid = useSelector(selectGroupGuid);

  return useQuery({
    queryKey: keys.allByGroup(group_guid),
    queryFn: () => fetchSitrepsByGroup(group_guid),
    staleTime: 0,
  });
};

const fetchSitrepById = async (sitrepId: UUID | undefined) => {
  const response = await axios.get<PollSitrepResponse>(
    `${config.apiGateway.sitrepsV2}/pollSitrep`,
    {
      params: { sitrep_id: sitrepId },
    }
  );
  return response.data;
};
export const useSitrepV2 = (sitrepId: UUID | undefined) => {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: keys.byId(sitrepId),
    queryFn: () => fetchSitrepById(sitrepId),
    enabled: Boolean(sitrepId), // Only run the query if sitrepId is truthy
    refetchInterval: (query) => {
      // Access the query state via queryClient
      const queryState = queryClient.getQueryState(keys.byId(sitrepId));

      // Stop polling if the query has encountered an error
      if (queryState?.status === 'error') {
        return false;
      }

      // If data is undefined or status is undefined, continue polling
      if (!query?.state?.data?.status) {
        return 500; // Continue polling every 500ms
      }

      const sitrepStatus = query?.state?.data?.status?.toLowerCase();
      // Stop polling if status is 'summarized' or 'error'
      if (
        sitrepStatus === 'summarized' ||
        sitrepStatus === 'error'
      ) {
        return false;
      }
      // Continue polling
      return 500;
    },
    refetchOnWindowFocus: false,
    refetchOnReconnect: true, // Allow refetching on reconnect in case of network issues
    refetchOnMount: false,
    retry: false, // Do not retry the query if it fails
  });
};

type InitSitRepMutationInputs = {
  sitrep_template_id: UUID;
  sitrep_source: string;
  sitrep_source_id: UUID;
  name: string;
  description: string;
  selectedDatetime: string;
  aor_id: UUID;
  incident_id: UUID;
};

type InitSitrepResponse = {
  sitrepId: UUID;
};

export const useInitSitrepV2 = () => {
  const queryClient = useQueryClient();
  const user_guid = useSelector(selectUserGuid);
  const group_guid = useSelector(selectGroupGuid);

  return useMutation<UUID, any, InitSitRepMutationInputs>({
    mutationFn: async ({
      sitrep_template_id,
      sitrep_source,
      sitrep_source_id,
      name,
      description,
      selectedDatetime,
      aor_id,
      incident_id,
    }) => {
      const response = await axios.post<InitSitrepResponse>(
        `${config.apiGateway.sitrepsV2}/initSitrep`,
        {
          sitrep_template_id,
          sitrep_source,
          sitrep_source_id,
          name,
          description,
          user_guid,
          group_guid,
          selectedDatetime,
          aor_id,
          incident_id,
        }
      );

      if (response.data && response.data.sitrepId) {
        return response.data.sitrepId;
      } else {
        throw new Error('Invalid response from server: sitrepId not found.');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.base });
    },
  });
};

type UpdateSitrepMutationInputs = {
  name?: string;
  description?: string;
  sitrep_id: UUID;
  archived?: boolean;
  aor_id?: UUID;
  incident_id?: UUID;
};
type UpdateSitrepOptions = Omit<UpdateSitrepMutationInputs, 'sitrep_id'> & {
  id: UUID;
};

const updateSitrep = async ({
  name,
  description,
  sitrep_id,
  archived,
  aor_id,
  incident_id,
}: UpdateSitrepMutationInputs) => {
  const payload: UpdateSitrepOptions = { id: sitrep_id };
  if (name !== undefined) payload.name = name;
  if (description !== undefined) payload.description = description;
  if (archived !== undefined) payload.archived = archived;
  if (aor_id !== undefined) payload.aor_id = aor_id;
  if (incident_id !== undefined) payload.incident_id = incident_id;

  const response = await axios.put<UpdateSitrepOptions>(
    `${config.apiGateway.sitrepsV2}/`,
    payload
  );
  return response.data;
};
export const useUpdateSitrepV2 = () => {
  const queryClient = useQueryClient();

  return useMutation<any, any, UpdateSitrepMutationInputs>({
    mutationFn: (vars) => updateSitrep(vars),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: keys.base });
    },
  });
};

type RefreshSitrepMutationInputs = {
  id: UUID;
  selectedDatetime: string;
  sections?: any[];
  hardRefresh: boolean;
};

export const useRefreshSitrepV2 = () => {
  const user_guid = useSelector(selectUserGuid);
  const group_guid = useSelector(selectGroupGuid);
  const queryClient = useQueryClient();

  return useMutation<any, any, RefreshSitrepMutationInputs>({
    mutationFn: async ({
      id,
      selectedDatetime,
      sections = [],
      hardRefresh,
    }) => {
      const response = await axios.post(
        `${config.apiGateway.sitrepsV2}/refreshSitrep`,
        {
          sitrepId: id,
          user_guid,
          group_guid,
          selectedDatetime,
          sections,
          hardRefresh,
        }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.base });
    },
  });
};

type UpdateSitrepSectionMutationInputs = {
  sitrepId: UUID;
  sectionId: UUID;
  updatedFields: {
    sectionText: string;
  };
};

export const useUpdateSitrepSectionV2 = () => {
  const queryClient = useQueryClient();

  return useMutation<
    UpdateSitrepSectionMutationInputs['sectionId'],
    Error,
    UpdateSitrepSectionMutationInputs
  >({
    mutationFn: async ({ sitrepId, sectionId, updatedFields }) => {
      const response = await axios.post(
        `${config.apiGateway.sitrepsV2}/updateSitrepSection`,
        {
          sitrepId,
          sectionId,
          ...updatedFields,
        }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.base });
    },
  });
};

type AddSitrepSectionMutationInputs = {
  sitrepId: UUID;
  sectionTemplateId: UUID;
  selectedDatetime: string;
  sitrep_source: string;
  sitrep_source_id: UUID;
  order: number;
  aor_id: UUID;
};
type AddSitrepSectionParams = AddSitrepSectionMutationInputs & {
  user_guid: UUID;
  group_guid: UUID;
};
export const addSitrepSection = async (
  vars: AddSitrepSectionMutationInputs,
  user_guid: UUID,
  group_guid: UUID
) => {
  const response = await axios.post<
    UUID,
    AxiosResponse<UUID>,
    AddSitrepSectionParams
  >(`${config.apiGateway.sitrepsV2}/addSitrepSection`, {
    ...vars,
    user_guid,
    group_guid,
  });
  return response.data;
};
export const useAddSitrepSectionV2 = () => {
  const user_guid = useSelector(selectUserGuid);
  const group_guid = useSelector(selectGroupGuid);
  const queryClient = useQueryClient();

  return useMutation<unknown, unknown, AddSitrepSectionMutationInputs>({
    mutationFn: (vars) => addSitrepSection(vars, user_guid, group_guid),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.base });
    },
  });
};

type RemoveSitrepSectionMutationInputs = {
  sitrepId: UUID;
  sectionId: UUID;
};

export const useRemoveSitrepSectionV2 = () => {
  const queryClient = useQueryClient();

  return useMutation<any, any, RemoveSitrepSectionMutationInputs>({
    mutationFn: async ({ sitrepId, sectionId }) => {
      const response = await axios.post(
        `${config.apiGateway.sitrepsV2}/removeSitrepSection`,
        {
          sitrepId,
          sectionId,
        }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.base });
    },
  });
};

type MoveSitrepSectionMutationInputs = {
  sitrepId: UUID;
  sectionId: UUID;
  direction: 'up' | 'down';
};

export const useMoveSitrepSectionV2 = () => {
  const queryClient = useQueryClient();

  return useMutation<any, any, MoveSitrepSectionMutationInputs>({
    mutationFn: async ({ sitrepId, sectionId, direction }) => {
      const response = await axios.post(
        `${config.apiGateway.sitrepsV2}/moveSection`,
        {
          sitrepId,
          sectionId,
          direction,
        }
      );
      return response.data;
    },
    onSuccess: (_, { sitrepId }) => {
      queryClient.invalidateQueries({ queryKey: keys.byId(sitrepId) });
    },
  });
};
